import { gql, useMutation, useQuery } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import {
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import { useGetMe } from 'components/Session/hooks'
import React, { useState } from 'react'
import { Timeline } from 'utils/__generated__/graphql'

import { TimelineDataType, TimelineForm } from '../Create'
import { TIMELINE_BASE_QUERIES, TIMELINE_FRAGMENT } from '../constants'

const CLONE_TIMELINE = gql`
  ${TIMELINE_FRAGMENT}
  mutation CLONE_TIMELINE($timeline: TimelineInput!) {
    cloneTimeline(timeline: $timeline) {
      ...TimelineFragment
    }
  }
`

function Clone({ timeline }: { timeline: Timeline }) {
  const [open, setOpen] = useState<boolean>(false)
  const { data: meData } = useGetMe()

  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [cloneTimeline] = useMutation(CLONE_TIMELINE)

  const handleTimeline = (data: TimelineDataType) => {
    cloneTimeline({
      variables: {
        timeline: {
          id: timeline.id,
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          isAdminTimeline: true,
          ...data,
        },
      },
      onCompleted: handleClose,
      update(cache, { data: { cloneTimeline } }) {
        cache.modify({
          fields: {
            findTimelines(existingTimelines = []) {
              const newTimelineRef = cache.writeFragment({
                data: cloneTimeline,
                fragment: gql`
                  fragment NewTimeline on Timeline {
                    id
                  }
                `,
              })
              return [newTimelineRef, ...existingTimelines]
            },
          },
        })
      },
    })
  }

  const { loading, data } = useQuery(TIMELINE_BASE_QUERIES, {
    variables: { siteFilter: { isPublished: true }, companyFilter: {} },
    skip: !open,
  })

  const sites = data?.sites || []
  const companies = data?.companies || []

  const formValues = {
    site: timeline.site?.id || '',
    name: `${timeline.name || ''} (Copy)`,
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Clone - {timeline.name}
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        {loading ? (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <TimelineForm
            values={formValues}
            onTimeline={handleTimeline}
            sites={sites}
            companies={companies}
          />
        )}
      </Modal>
      <IconButton onClick={handleOpen}>
        <Tooltip title="Clone" placement="top">
          <FileCopyIcon />
        </Tooltip>
      </IconButton>
    </React.Fragment>
  )
}

export default Clone
